import React, {
  useEffect,
  useState,
} from "react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
} from "recharts";

import {
  getFmiWeather,
  getFmiWeatherDay,
} from "../../../api/api";


// -------------------------------------------------------
// TODAY
// -------------------------------------------------------

const getToday = () => {
  const now = new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      now.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
const getWeatherEmoji = (item) => {
  const cloudiness = Number(item.cloudiness);
  const precipitation = Number(item.precipitation);
  const temperature = Number(item.temperature);

  // Snow
  if (
    Number.isFinite(precipitation) &&
    precipitation > 0 &&
    Number.isFinite(temperature) &&
    temperature <= 1
  ) {
    return "❄️";
  }

  // Rain
  if (
    Number.isFinite(precipitation) &&
    precipitation > 0
  ) {
    return "🌧️";
  }

  // Cloudiness from FMI: 0–100 %
  if (Number.isFinite(cloudiness)) {
    if (cloudiness <= 10) return "☀️";
    if (cloudiness <= 30) return "🌤️";
    if (cloudiness <= 60) return "⛅";
    if (cloudiness <= 85) return "🌥️";

    return "☁️";
  }

  return "";
};

// -------------------------------------------------------
// COMPONENT
// -------------------------------------------------------

export default function WeatherBlock({
  data = {},
  onChange,
  kiinteisto,
}) {

  const createForm = (
    source = {}
  ) => ({
    date:
      source.date ||
      getToday(),

    startTime:
      source.startTime ??
      "",

    endTime:
      source.endTime ??
      "",

    humidity:
      source.humidity ??
      "",

    temperature:
      source.temperature ??
      "",

    windSpeed:
      source.windSpeed ??
      "",

    windDirection:
      source.windDirection ??
      "",
  });


  // -------------------------------------------------------
  // STATE
  // -------------------------------------------------------

  const [
    form,
    setForm,
  ] = useState(
    () =>
      createForm(data)
  );


  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    dayLoading,
    setDayLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


const [
  dayWeather,
  setDayWeather,
] = useState(
  () => Array.isArray(data?.dayWeather)
    ? data.dayWeather
    : []
);

  // -------------------------------------------------------
  // PROPERTY LOCATION
  //
  // sijainti.coordinates:
  // [longitude, latitude]
  // -------------------------------------------------------

  const rakennus =
    kiinteisto
      ?.rakennukset_fulls
      ?.find(
        (r) =>
          Array.isArray(
            r?.sijainti
              ?.coordinates
          ) &&
          r.sijainti
            .coordinates
            .length >= 2
      );


  const coordinates =
    rakennus
      ?.sijainti
      ?.coordinates;


  const longitude =
    coordinates?.[0] != null
      ? Number(
          coordinates[0]
        )
      : null;


  const latitude =
    coordinates?.[1] != null
      ? Number(
          coordinates[1]
        )
      : null;


  const hasCoordinates =
    Number.isFinite(
      latitude
    ) &&
    Number.isFinite(
      longitude
    );


  // -------------------------------------------------------
  // SYNC EXTERNAL DATA
  // -------------------------------------------------------

useEffect(() => {
  setForm(
    createForm(data)
  );

  setDayWeather(
    Array.isArray(data?.dayWeather)
      ? data.dayWeather
      : []
  );
}, [data]);


  // -------------------------------------------------------
  // LOCAL EDITING
  // -------------------------------------------------------

  const updateLocal = (
    field,
    value
  ) => {

    setForm(
      (prev) => ({
        ...prev,
        [field]: value,
      })
    );

  };


  const saveField = (
    field,
    value
  ) => {

    const next = {
      ...form,
      [field]: value,
    };


    setForm(next);


    onChange?.({
      ...data,
      ...next,
    });

  };


  // -------------------------------------------------------
  // FMI WEATHER FOR SELECTED TIME
  // -------------------------------------------------------

  const fetchWeather =
    async () => {

      if (
        !hasCoordinates
      ) {

        console.error(
          "WeatherBlock: coordinates missing",
          {
            kiinteisto,
            rakennus,
            coordinates,
          }
        );


        setError(
          "Kiinteistön koordinaatteja ei löytynyt."
        );

        return;
      }


      if (!form.date) {

        setError(
          "Valitse päivämäärä."
        );

        return;
      }


      if (
        !form.startTime
      ) {

        setError(
          "Valitse aloitusaika."
        );

        return;
      }


      setLoading(true);
      setError("");


      try {

        console.log(
          "FMI WEATHER REQUEST:",
          {
            lat:
              latitude,

            lon:
              longitude,

            date:
              form.date,

            startTime:
              form.startTime,

            endTime:
              form.endTime,
          }
        );


        const response =
          await getFmiWeather({
            lat:
              latitude,

            lon:
              longitude,

            date:
              form.date,

            startTime:
              form.startTime,

            endTime:
              form.endTime,
          });


        const weather =
          response.data;


        console.log(
          "FMI WEATHER:",
          weather
        );


        const next = {
          ...form,

          humidity:
            weather
              ?.humidity ??
            "",

          temperature:
            weather
              ?.temperature ??
            "",

          windSpeed:
            weather
              ?.windSpeed ??
            "",

          windDirection:
            weather
              ?.windDirectionText ??
            weather
              ?.windDirection ??
            "",
        };


        setForm(next);


        onChange?.({
          ...data,
          ...next,
        });


      } catch (err) {

        console.error(
          "FMI weather error:",
          err
        );


        const backendError =
          err
            ?.response
            ?.data
            ?.error;


        const status =
          err
            ?.response
            ?.status;


        if (backendError) {

          setError(
            backendError
          );

        } else if (status) {

          setError(
            `Säätietojen haku epäonnistui (${status}).`
          );

        } else {

          setError(
            err?.message ||
              "Säätietojen hakeminen epäonnistui."
          );

        }

      } finally {

        setLoading(false);

      }

    };


  // -------------------------------------------------------
  // FMI WHOLE DAY 00-23
  // -------------------------------------------------------

  const fetchWeatherDay =
    async () => {

      if (
        !hasCoordinates
      ) {

        setError(
          "Kiinteistön koordinaatteja ei löytynyt."
        );

        return;
      }


      if (!form.date) {

        setError(
          "Valitse päivämäärä."
        );

        return;
      }


      setDayLoading(true);
      setError("");


      try {

        console.log(
          "FMI DAY REQUEST:",
          {
            lat:
              latitude,

            lon:
              longitude,

            date:
              form.date,
          }
        );


        const response =
          await getFmiWeatherDay({
            lat:
              latitude,

            lon:
              longitude,

            date:
              form.date,
          });


        const weather =
          response.data;


        console.log(
          "FMI DAY WEATHER:",
          weather
        );


        const chartData =
          (
            weather?.data ||
            []
          ).map(
            (item) => {

              const date =
                new Date(
                  item.time
                );


            return {
  ...item,

  timestamp: date.getTime(),

  hour: date.toLocaleTimeString(
    "fi-FI",
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/Helsinki",
    }
  ),

  weatherEmoji: getWeatherEmoji(item),
};

            }
          )
          .sort(
            (a, b) =>
              a.timestamp -
              b.timestamp
          );


        console.table(
          chartData
        );
console.log("FIRST WEATHER ITEM:", chartData[0]);
console.log("WEATHER KEYS:", Object.keys(chartData[0] || {}));

                setDayWeather(chartData);

          onChange?.({
            ...data,
            ...form,
            dayWeather: chartData,
          });

        if (
          chartData.length ===
          0
        ) {

          setError(
            "Päivän säätietoja ei löytynyt."
          );

        }


      } catch (err) {

        console.error(
          "FMI day weather error:",
          err
        );


        setDayWeather([]);


        const backendError =
          err
            ?.response
            ?.data
            ?.error;


        const status =
          err
            ?.response
            ?.status;


        if (backendError) {

          setError(
            backendError
          );

        } else if (status) {

          setError(
            `Päivän säätietojen haku epäonnistui (${status}).`
          );

        } else {

          setError(
            err?.message ||
              "Päivän säätietojen hakeminen epäonnistui."
          );

        }


      } finally {

        setDayLoading(
          false
        );

      }

    };


  // -------------------------------------------------------
  // FIELD
  // -------------------------------------------------------

  const Field = ({
    label,
    field,
    unit = "",
    type = "text",
  }) => (

    <div>

      <label
        className="form-label fw-semibold mb-1"

        style={{
          fontSize:
            "0.85rem",
        }}
      >
        {label}
      </label>


      <div className="input-group input-group-sm">

        <input
          type={type}

          className="form-control"

          value={
            form[field] ??
            ""
          }

          onChange={(e) =>
            updateLocal(
              field,
              e.target.value
            )
          }

          onBlur={(e) =>
            saveField(
              field,
              e.target.value
            )
          }
        />


        {unit && (

          <span className="input-group-text">
            {unit}
          </span>

        )}

      </div>

    </div>

  );


  // -------------------------------------------------------
  // SELECTED INSPECTION TIME RANGE
  // -------------------------------------------------------

  const getSelectedTimestamp = (
    time
  ) => {

    if (
      !form.date ||
      !time
    ) {

      return null;

    }


    const [
      hours,
      minutes,
    ] =
      time
        .split(":")
        .map(Number);


    /*
     * Create the timestamp in the browser's
     * local timezone.
     *
     * In your app this corresponds to the
     * inspection's Finnish local time.
     */
    const selectedDate =
      new Date(
        `${form.date}T00:00:00`
      );


    selectedDate.setHours(
      hours,
      minutes || 0,
      0,
      0
    );


    return selectedDate.getTime();

  };


  const selectedStartTimestamp =
    getSelectedTimestamp(
      form.startTime
    );


  const selectedEndTimestamp =
    getSelectedTimestamp(
      form.endTime ||
      form.startTime
    );


  // -------------------------------------------------------
  // SELECTED WEATHER POINT
  // -------------------------------------------------------

  const selectedWeather =
    selectedStartTimestamp !==
      null &&
    dayWeather.length > 0

      ? dayWeather.reduce(
          (
            closest,
            item
          ) => {

            if (!closest) {

              return item;

            }


            const currentDifference =
              Math.abs(
                item.timestamp -
                  selectedStartTimestamp
              );


            const closestDifference =
              Math.abs(
                closest.timestamp -
                  selectedStartTimestamp
              );


            return (
              currentDifference <
              closestDifference
            )
              ? item
              : closest;

          },
          null
        )

      : null;


  // -------------------------------------------------------
  // CHART DOMAIN
  // -------------------------------------------------------

  const chartMinTimestamp =
    dayWeather.length > 0
      ? dayWeather[0]
          .timestamp
      : null;


  const chartMaxTimestamp =
    dayWeather.length > 0
      ? dayWeather[
          dayWeather.length - 1
        ].timestamp
      : null;


  // Clamp selected range to graph.
  const highlightStart =
    selectedStartTimestamp !==
      null &&
    chartMinTimestamp !== null &&
    chartMaxTimestamp !== null

      ? Math.max(
          chartMinTimestamp,
          Math.min(
            selectedStartTimestamp,
            chartMaxTimestamp
          )
        )

      : null;


  const highlightEnd =
    selectedEndTimestamp !==
      null &&
    chartMinTimestamp !== null &&
    chartMaxTimestamp !== null

      ? Math.max(
          chartMinTimestamp,
          Math.min(
            selectedEndTimestamp,
            chartMaxTimestamp
          )
        )

      : null;


  // If start/end are identical,
  // make the highlight one hour wide.
  const effectiveHighlightEnd =
    highlightStart !== null &&
    highlightEnd !== null

      ? (
          highlightEnd ===
          highlightStart

            ? Math.min(
                highlightStart +
                  60 *
                    60 *
                    1000,

                chartMaxTimestamp
              )

            : highlightEnd
        )

      : null;


  // -------------------------------------------------------
  // DATE LABEL
  // -------------------------------------------------------

  const dayLabel =
    form.date
      ? new Date(
          `${form.date}T12:00:00`
        ).toLocaleDateString(
          "fi-FI",
          {
            weekday:
              "long",

            day:
              "numeric",

            month:
              "numeric",
          }
        )
      : "";


  // -------------------------------------------------------
  // TIME FORMATTER
  // -------------------------------------------------------

  const formatChartHour = (
    timestamp
  ) => {

    if (
      timestamp == null
    ) {

      return "";

    }


    return new Date(
      timestamp
    ).toLocaleTimeString(
      "fi-FI",
      {
        hour:
          "2-digit",

        minute:
          "2-digit",

        hour12:
          false,

        timeZone:
          "Europe/Helsinki",
      }
    );

  };


  // -------------------------------------------------------
  // CUSTOM TOOLTIP
  // -------------------------------------------------------

  const WeatherTooltip = ({
    active,
    payload,
  }) => {

    if (
      !active ||
      !payload?.length
    ) {

      return null;

    }


    const point =
      payload[0]
        ?.payload;


    if (!point) {

      return null;

    }


    return (

      <div
        className="bg-white border rounded shadow-sm p-2"

        style={{
          fontSize:
            "0.8rem",
        }}
      >

        <div className="fw-bold mb-1">
          {point.hour}
        </div>


        <div>

          Lämpötila:{" "}

          <strong
            style={{
              color:
                "#f44336",
            }}
          >

            {point.temperature ??
              "-"}{" "}
            °C

          </strong>

        </div>


        <div>

          Kosteus:{" "}

          {point.humidity ??
            "-"}{" "}
          %

        </div>


        <div>

          Tuuli:{" "}

          {point.windSpeed ??
            "-"}{" "}
          m/s

        </div>


        <div>

          Tuulen suunta:{" "}

          {point
            .windDirectionText ||
            "-"}

        </div>

      </div>

    );

  };


  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------

  return (

    <div className="border rounded p-3 bg-white">


      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="d-flex justify-content-between align-items-center mb-3">


        <h6
          className="fw-bold mb-0"

          style={{
            color:
              "#04aa00",
          }}
        >
          Ulkoilman olosuhteet
        </h6>


        <div className="d-flex gap-2">


          {/* SINGLE WEATHER */}

          <button
            type="button"

            className="btn btn-sm btn-outline-success"

            onClick={
              fetchWeather
            }

            disabled={
              loading ||
              dayLoading ||
              !hasCoordinates
            }

            title={
              !hasCoordinates
                ? "Kiinteistön koordinaatteja ei löytynyt"
                : "Hae säätiedot FMI:ltä"
            }
          >

            {loading ? (

              <>

                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />

                Haetaan...

              </>

            ) : (

              <>

                <i className="bi bi-cloud-sun me-1" />

                Hae säätiedot

              </>

            )}

          </button>


          {/* WHOLE DAY */}

          <button
            type="button"

            className="btn btn-sm btn-outline-primary"

            onClick={
              fetchWeatherDay
            }

            disabled={
              loading ||
              dayLoading ||
              !hasCoordinates ||
              !form.date
            }

            title={
              !hasCoordinates
                ? "Kiinteistön koordinaatteja ei löytynyt"
                : !form.date
                ? "Valitse päivämäärä"
                : "Hae koko päivän säätiedot"
            }
          >

            {dayLoading ? (

              <>

                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />

                Haetaan...

              </>

            ) : (

              <>

                <i className="bi bi-graph-up me-1" />

                Hae päivän sää

              </>

            )}

          </button>

        </div>

      </div>


      {/* ===================================================
          DATE / TIME
      =================================================== */}

      <div className="row g-2 mb-3">


        {/* DATE */}

        <div className="col-md-4">

          <Field
            label="Päivämäärä"
            field="date"
            type="date"
          />

        </div>


        {/* START TIME */}

        <div className="col-md-4">

          <label
            className="form-label fw-semibold mb-1"

            style={{
              fontSize:
                "0.85rem",
            }}
          >
            Alkuaika
          </label>


          <select
            className="form-select form-select-sm"

            value={
              form.startTime ??
              ""
            }

            onChange={(e) =>
              updateLocal(
                "startTime",
                e.target.value
              )
            }

            onBlur={(e) =>
              saveField(
                "startTime",
                e.target.value
              )
            }
          >

            <option value="">
              --
            </option>


            {Array.from(
              {
                length: 24,
              },

              (_, hour) => (

                <option
                  key={hour}

                  value={`${hour}:00`}
                >
                  {hour}:00
                </option>

              )
            )}

          </select>

        </div>


        {/* END TIME */}

        <div className="col-md-4">

          <label
            className="form-label fw-semibold mb-1"

            style={{
              fontSize:
                "0.85rem",
            }}
          >
            Loppuaika
          </label>


          <select
            className="form-select form-select-sm"

            value={
              form.endTime ??
              ""
            }

            onChange={(e) =>
              updateLocal(
                "endTime",
                e.target.value
              )
            }

            onBlur={(e) =>
              saveField(
                "endTime",
                e.target.value
              )
            }
          >

            <option value="">
              --
            </option>


            {Array.from(
              {
                length: 24,
              },

              (_, hour) => (

                <option
                  key={hour}

                  value={`${hour}:00`}
                >
                  {hour}:00
                </option>

              )
            )}

          </select>

        </div>

      </div>


      {/* ===================================================
          ERROR
      =================================================== */}

      {error && (

        <div
          className="alert alert-danger py-2 mb-3"

          style={{
            fontSize:
              "0.85rem",
          }}
        >

          {error}

        </div>

      )}


      {/* ===================================================
          WEATHER VALUES
      =================================================== */}

      <div className="row g-2">


        <div className="col-md">

          <Field
            label="Kosteus"
            field="humidity"
            unit="RH%"
          />

        </div>


        <div className="col-md">

          <Field
            label="Lämpötila"
            field="temperature"
            unit="°C"
          />

        </div>


        <div className="col-md">

          <Field
            label="Tuuli"
            field="windSpeed"
            unit="m/s"
          />

        </div>


        <div className="col-md">

          <Field
            label="Tuulen suunta"
            field="windDirection"
          />

        </div>

      </div>


      {/* ===================================================
          FMI DAY GRAPH
      =================================================== */}

      {(dayLoading ||
        dayWeather.length >
          0) && (

        <div
          className="mt-4 pt-3 border-top"
        >


          {/* DATE */}

          <div
            className="fw-bold mb-1"

            style={{
              fontSize:
                "0.9rem",

              textTransform:
                "capitalize",
            }}
          >
            {dayLabel}
          </div>


          {/* SELECTED TIME LABEL */}

          {form.startTime && (

            <div
              className="mb-2"

              style={{
                fontSize:
                  "0.75rem",

                color:
                  "#666",
              }}
            >

              Valittu tarkastusaika:{" "}

              <strong>
                {form.startTime}

                {form.endTime &&
                  form.endTime !==
                    form.startTime &&
                  ` – ${form.endTime}`}
              </strong>

            </div>

          )}
{/* WEATHER EMOJIS */}

<div
  style={{
    marginLeft: "46px",
    marginRight: "16px",
    display: "grid",
    gridTemplateColumns: `repeat(${dayWeather.length}, minmax(0, 1fr))`,
    textAlign: "center",
    marginBottom: "4px",
  }}
>
  {dayWeather.map((item, index) => (
    <div
      key={`weather-${item.time}-${index}`}
      style={{
        fontSize: "15px",
        lineHeight: "20px",
        minWidth: 0,
      }}
    >
      {item.weatherEmoji || ""}
    </div>
  ))}
</div>

          {dayLoading ? (

            <div
              className="d-flex justify-content-center align-items-center"

              style={{
                height:
                  "320px",
              }}
            >

              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />

              Haetaan päivän säätietoja...

            </div>

          ) : (

            <>


              {/* -------------------------------------------
                  HOURS ABOVE GRAPH
              ------------------------------------------- */}

              <div
                style={{
                  marginLeft:
                    "46px",

                  marginRight:
                    "16px",

                  display:
                    "grid",

                  gridTemplateColumns:
                    `repeat(${dayWeather.length}, minmax(0, 1fr))`,

                  textAlign:
                    "center",

                  fontSize:
                    "9px",

                  fontWeight:
                    "600",

                  color:
                    "#555",

                  marginBottom:
                    "3px",
                }}
              >

                {dayWeather.map(
                  (
                    item,
                    index
                  ) => (

                    <div
                      key={
                        `${item.time}-${index}`
                      }
                    >

                      {
                        item.hour
                          ?.split(
                            ":"
                          )[0]
                      }

                    </div>

                  )
                )}

              </div>


              {/* -------------------------------------------
                  TEMPERATURE CHART
              ------------------------------------------- */}

              <div
                style={{
                  width:
                    "100%",

                  height:
                    "230px",
                }}
              >

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <LineChart
                    data={
                      dayWeather
                    }

                    margin={{
                      top: 5,
                      right: 16,
                      left: 0,
                      bottom: 0,
                    }}
                  >


                    <CartesianGrid
                      vertical={
                        false
                      }

                      stroke="#e5e5e5"

                      strokeDasharray="0"
                    />


                    {/* IMPORTANT:
                        Numeric time axis so ReferenceArea
                        can highlight arbitrary times. */}

                    <XAxis
                      dataKey="timestamp"

                      type="number"

                      scale="time"

                      domain={[
                        "dataMin",
                        "dataMax",
                      ]}

                      hide
                    />


                    <YAxis
                      width={46}

                      axisLine={
                        false
                      }

                      tickLine={
                        false
                      }

                      tick={{
                        fontSize:
                          9,

                        fill:
                          "#f44336",
                      }}

                      domain={[
                        "auto",
                        "auto",
                      ]}
                    />


                    {/* -----------------------------------
                        SELECTED TIME RANGE
                    ----------------------------------- */}

                    {highlightStart !==
                      null &&
                      effectiveHighlightEnd !==
                        null && (

                      <ReferenceArea
                        x1={
                          Math.min(
                            highlightStart,
                            effectiveHighlightEnd
                          )
                        }

                        x2={
                          Math.max(
                            highlightStart,
                            effectiveHighlightEnd
                          )
                        }

                        fill="#bdbdbd"

                        fillOpacity={
                          0.45
                        }

                        stroke="#777"

                        strokeOpacity={
                          0.7
                        }

                        strokeWidth={
                          1
                        }

                        ifOverflow="visible"
                      />

                    )}


                    {/* START TIME LINE */}

                    {highlightStart !==
                      null && (

                      <ReferenceLine
                        x={
                          highlightStart
                        }

                        stroke="#666"

                        strokeWidth={
                          1
                        }

                        strokeDasharray="4 3"
                      />

                    )}


                    {/* END TIME LINE */}

                    {effectiveHighlightEnd !==
                      null &&
                      effectiveHighlightEnd !==
                        highlightStart && (

                      <ReferenceLine
                        x={
                          effectiveHighlightEnd
                        }

                        stroke="#666"

                        strokeWidth={
                          1
                        }

                        strokeDasharray="4 3"
                      />

                    )}


                    <Tooltip
                      content={
                        <WeatherTooltip />
                      }

                      labelFormatter={
                        formatChartHour
                      }
                    />


                    {/* RED TEMPERATURE LINE */}

                    <Line
                      type="monotone"

                      dataKey="temperature"

                      name="Lämpötila"

                      stroke="#ff3b3b"

                      strokeWidth={
                        2.5
                      }

                      dot={
                        false
                      }

                      activeDot={{
                        r: 5,

                        fill:
                          "#ffffff",

                        stroke:
                          "#ff3b3b",

                        strokeWidth:
                          2,
                      }}

                      connectNulls

                      isAnimationActive={
                        false
                      }
                    />


                  </LineChart>

                </ResponsiveContainer>

              </div>


              {/* TEMPERATURE UNIT */}

              <div
                style={{
                  color:
                    "#f44336",

                  fontSize:
                    "10px",

                  fontWeight:
                    "600",

                  marginLeft:
                    "8px",

                  marginTop:
                    "-3px",
                }}
              >
                °C
              </div>


              {/* -------------------------------------------
                  WIND ROW
              ------------------------------------------- */}

              <div
                style={{
                  marginLeft:
                    "46px",

                  marginRight:
                    "16px",

                  marginTop:
                    "3px",

                  paddingTop:
                    "5px",

                  borderTop:
                    "1px solid #ddd",

                  display:
                    "grid",

                  gridTemplateColumns:
                    `repeat(${dayWeather.length}, minmax(0, 1fr))`,

                  textAlign:
                    "center",
                }}
              >

                {dayWeather.map(
                  (
                    item,
                    index
                  ) => {

                    const direction =
                      Number(
                        item.windDirection
                      );


                    const hasDirection =
                      Number.isFinite(
                        direction
                      );


                    const itemHour =
                      Number(
                        item.hour
                          ?.split(":")[0]
                      );


                    const startHour =
                      form.startTime
                        ? Number(
                            form.startTime.split(
                              ":"
                            )[0]
                          )
                        : null;


                    const endHour =
                      form.endTime
                        ? Number(
                            form.endTime.split(
                              ":"
                            )[0]
                          )
                        : startHour;


                    const isSelected =
                      startHour !== null &&
                      endHour !== null &&
                      itemHour >=
                        Math.min(
                          startHour,
                          endHour
                        ) &&
                      itemHour <=
                        Math.max(
                          startHour,
                          endHour
                        );


                    return (

                      <div
                        key={
                          `wind-${item.time}-${index}`
                        }

                        style={{
                          minWidth:
                            0,

                          background:
                            isSelected
                              ? "rgba(189, 189, 189, 0.28)"
                              : "transparent",

                          borderRadius:
                            "2px",
                        }}
                      >


                        {/* WIND ARROW */}

                        <div
                          style={{
                            height:
                              "16px",

                            display:
                              "flex",

                            alignItems:
                              "center",

                            justifyContent:
                              "center",

                            color:
                              "#57b847",

                            fontSize:
                              "12px",
                          }}
                        >

                          {hasDirection ? (

                            <span
                              style={{
                                display:
                                  "inline-block",

                                transform:
                                  `rotate(${direction}deg)`,

                                transformOrigin:
                                  "center",
                              }}
                            >
                              ↑
                            </span>

                          ) : (

                            <span>
                              -
                            </span>

                          )}

                        </div>


                        {/* WIND SPEED */}

                        <div
                          style={{
                            fontSize:
                              "9px",

                            color:
                              "#666",

                            fontWeight:
                              isSelected
                                ? "700"
                                : "400",
                          }}
                        >

                          {item.windSpeed !=
                          null
                            ? Number(
                                item.windSpeed
                              ).toFixed(
                                0
                              )
                            : "-"}

                        </div>


                      </div>

                    );

                  }
                )}

              </div>


              {/* WIND UNIT */}

              <div
                style={{
                  textAlign:
                    "right",

                  paddingRight:
                    "16px",

                  fontSize:
                    "9px",

                  color:
                    "#777",

                  marginTop:
                    "1px",
                }}
              >
                m/s
              </div>


              {/* -------------------------------------------
                  SELECTED TIME SUMMARY
              ------------------------------------------- */}

              {selectedWeather && (

                <div
                  className="mt-3 pt-3"

                  style={{
                    borderTop:
                      "1px solid #ddd",
                  }}
                >

                  <div className="row align-items-center">


                    {/* DATE / TIME */}

                    <div className="col-md-3">

                      <div
                        className="fw-semibold"

                        style={{
                          fontSize:
                            "11px",

                          textTransform:
                            "capitalize",
                        }}
                      >
                        {dayLabel}
                      </div>


                      <div
                        className="fw-bold"

                        style={{
                          fontSize:
                            "21px",

                          lineHeight:
                            1.2,
                        }}
                      >

                        {form.startTime ||
                          selectedWeather.hour}

                        {form.endTime &&
                          form.endTime !==
                            form.startTime &&
                          `–${form.endTime}`}

                      </div>

                    </div>


                    {/* TEMPERATURE */}

                    <div className="col-md-3">

                      <div
                        style={{
                          display:
                            "flex",

                          alignItems:
                            "center",

                          gap:
                            "8px",
                        }}
                      >

                        <div
                          style={{
                            fontSize:
                              "34px",

                            lineHeight:
                              1,
                          }}
                        >
                          🌡️
                        </div>


                        <div
                          style={{
                            color:
                              "#f44336",

                            fontSize:
                              "20px",

                            fontWeight:
                              "700",

                            whiteSpace:
                              "nowrap",
                          }}
                        >

                          {selectedWeather.temperature !=
                          null
                            ? `${
                                Number(
                                  selectedWeather.temperature
                                ) >
                                0
                                  ? "+"
                                  : ""
                              }${selectedWeather.temperature} °C`
                            : "-"}

                        </div>

                      </div>

                    </div>


                    {/* DETAILS */}

                    <div
                      className="col-md-6"

                      style={{
                        fontSize:
                          "12px",
                      }}
                    >


                      <div>

                        <strong>
                          Kosteus:
                        </strong>{" "}

                        {selectedWeather.humidity ??
                          "-"}{" "}
                        %

                      </div>


                      <div>

                        <strong>
                          Tuuli:
                        </strong>{" "}

                        {selectedWeather.windSpeed ??
                          "-"}{" "}
                        m/s

                      </div>


                      <div>

                        <strong>
                          Tuulen suunta:
                        </strong>{" "}

                        {selectedWeather.windDirectionText ||
                          "-"}

                      </div>


                    </div>


                  </div>

                </div>

              )}


            </>

          )}

        </div>

      )}


      {/* ===================================================
          LOCATION
      =================================================== */}

      {hasCoordinates ? (

        <div
          className="text-muted mt-3"

          style={{
            fontSize:
              "0.75rem",
          }}
        >

          Säätiedot haetaan sijainnille{" "}

          {latitude},{" "}
          {longitude}

        </div>

      ) : (

        <div
          className="text-danger mt-2"

          style={{
            fontSize:
              "0.75rem",
          }}
        >

          Kiinteistön sijaintitietoa ei löytynyt.

        </div>

      )}


    </div>

  );

}